import React from 'react';
import {flushSync} from 'react-dom';
import {Tooltip} from 'antd';
import {BsMoonStars, BsSun} from 'react-icons/bs';
import {useIntl} from 'react-intl';
import {useDarkMode} from '../../../../../shared/hooks/useDarkMode';
import './index.style.less';

// The new theme spreads out as a circle from the button (the browser's View
// Transitions); without them, or when the user asks for less motion, the
// theme switches at once as before.
const revealTheme = (button: HTMLElement, applyTheme: () => void) => {
  const doc = document as any;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (!doc.startViewTransition || reduceMotion) {
    applyTheme();
    return;
  }
  const box = button.getBoundingClientRect();
  const x = box.left + box.width / 2;
  const y = box.top + box.height / 2;
  const radius = Math.hypot(
    Math.max(x, window.innerWidth - x),
    Math.max(y, window.innerHeight - y),
  );
  const transition = doc.startViewTransition(() => flushSync(applyTheme));
  transition.ready.then(() => {
    document.documentElement.animate(
      {clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`]},
      {duration: 850, easing: 'ease-in-out', pseudoElement: '::view-transition-new(root)'},
    );
  });
};

// The navbar's dark mode switch: a moon in light mode, a sun in dark mode.
// Hidden on small screens, where the user menu carries the switch instead.
const ThemeToggle = () => {
  const {messages} = useIntl();
  const [dark, setDark] = useDarkMode();
  const label = messages[
    dark ? 'touch.darkMode.off' : 'touch.darkMode.on'
  ].toString();

  return (
    <Tooltip title={label}>
      <button
        type='button'
        className='app-theme-toggle'
        aria-label={label}
        aria-pressed={dark}
        onClick={(event) => revealTheme(event.currentTarget, () => setDark(!dark))}>
        {dark ? <BsSun /> : <BsMoonStars />}
      </button>
    </Tooltip>
  );
};

export default ThemeToggle;
